import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { Toggle } from "@/components/ui/toggle";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ImagePlus, Trash2, ZoomIn, ZoomOut, RotateCcw, Type as TypeIcon,
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight, MousePointerClick, Replace, Bold,
  Crop as CropIcon, PaintBucket, CopyPlus,
} from "lucide-react";

const NUDGE = 0.04;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 10;

const FONT_OPTIONS = [
  { value: "sans", label: "Sans (Helvetica)" },
  { value: "serif", label: "Serif (Times)" },
  { value: "mono", label: "Mono (Courier)" },
];
const COLOR_SWATCHES = ["#111111", "#ffffff", "#dc2626", "#2563eb", "#16a34a", "#f59e0b", "#7c3aed", "#db2777"];

export function TextStyleControls({ family, bold, color, onChange, idPrefix = "", isDark = true }) {
  return (
    <div className={`space-y-3 ${isDark ? "text-slate-100" : "text-slate-800"}`}>
      <div className="space-y-1.5">
        <span className="text-xs font-semibold tracking-wide text-muted-foreground">FONT</span>
        <div className="flex items-center gap-2">
          <Select value={family} onValueChange={(v) => onChange({ fontFamily: v })}>
            <SelectTrigger data-testid={`${idPrefix}text-font-select`} className={`h-9 flex-1 rounded-xl border ${isDark ? "border-blue-400/20 bg-[#0b1733] text-slate-100" : "border-slate-300 bg-white text-slate-800"}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDark ? "border-blue-400/20 bg-[#07132e] text-slate-100" : "border-slate-200 bg-white text-slate-800"}>
              {FONT_OPTIONS.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Toggle pressed={bold} onPressedChange={(v) => onChange({ bold: v })}
            data-testid={`${idPrefix}text-bold-toggle`} aria-label="Tebal"
            className={`h-9 w-9 rounded-xl border data-[state=on]:bg-blue-600 data-[state=on]:text-white ${isDark ? "border-blue-400/20 bg-[#0b1733] text-slate-200" : "border-slate-300 bg-white text-slate-700"}`}>
            <Bold className="h-4 w-4" />
          </Toggle>
        </div>
      </div>
      <div className="space-y-1.5">
        <span className="text-xs font-semibold tracking-wide text-muted-foreground">WARNA</span>
        <div className="flex items-center gap-2">
          <input type="color" value={color || "#111111"}
            data-testid={`${idPrefix}text-color-input`}
            onChange={(e) => onChange({ color: e.target.value })}
            className={`h-9 w-10 cursor-pointer rounded-lg border p-1 ${isDark ? "border-blue-400/20 bg-[#0b1733]" : "border-slate-300 bg-white"}`} />
          <div className="flex flex-wrap gap-1.5">
            {COLOR_SWATCHES.map((c) => (
              <button key={c} type="button" data-testid={`${idPrefix}text-color-swatch-${c}`}
                onClick={() => onChange({ color: c })}
                className={`h-6 w-6 rounded-full border ${
                  isDark ? "border-[#94a3b8]" : "border-[#64748b]"
                } ${color === c ? "ring-2 ring-primary ring-offset-1" : ""}`}
                style={{ background: c }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Inspector({
  theme = "dark", language = "id", selected, cell, defaultFont, onUploadImage, onSetText, onSetColor, onDelete, onChangeTransform, onResetTransform, onCrop, onDuplicate,
}) {
  const isDark = theme === "dark";
  const isEnglish = language === "en";
  const fileRef = useRef(null);
  const [textValue, setTextValue] = useState("");
  const [family, setFamily] = useState("sans");
  const [bold, setBold] = useState(true);
  const [color, setColor] = useState("#111111");
  const [pinColor, setPinColor] = useState("#2563eb");

  useEffect(() => {
    if (cell?.type === "color" && cell.fillColor) setPinColor(cell.fillColor);
  }, [selected?.pageIdx, selected?.cellIdx, cell?.type, cell?.fillColor]);

  useEffect(() => {
    const df = defaultFont || { fontFamily: "sans", bold: true, color: "#111111" };
    const isText = cell?.type === "text";
    setTextValue(isText ? cell.text || "" : "");
    setFamily(isText ? (cell.fontFamily || "sans") : df.fontFamily);
    setBold(isText ? (cell.bold ?? true) : df.bold);
    setColor(isText ? (cell.color || "#111111") : df.color);
  }, [selected?.pageIdx, selected?.cellIdx, cell?.type, cell?.text, cell?.fontFamily, cell?.bold, cell?.color, defaultFont]);

  if (!selected || !cell) {
    return (
      <div data-testid="editor-inspector-panel"
        className={`pms-inspector-empty-theme flex h-full flex-col items-center justify-center rounded-2xl border p-8 text-center ${isDark ? "border-[#29466f] bg-[#07132e] text-slate-100" : "border-[#c7dcf8] bg-[#f0f6ff] text-slate-800"}` }>
        <MousePointerClick className="h-8 w-8 text-muted-foreground" />
        <p className="mt-3 text-sm font-medium">{isEnglish ? "Select a position" : "Pilih sebuah posisi"}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {isEnglish ? "Click a circle on the page to upload an image or add text." : "Klik salah satu lingkaran pada halaman untuk mengunggah gambar atau menambah teks."}
        </p>
      </div>
    );
  }

  const posLabel = `${isEnglish ? "Position" : "Posisi"} ${selected.cellIdx + 1} · ${isEnglish ? "Page" : "Halaman"} ${selected.pageIdx + 1}`;
  const zoom = cell.zoom || 1;

  const nudge = (dx, dy) =>
    onChangeTransform({
      offX: Math.max(-1.5, Math.min(1.5, (cell.offX || 0) + dx)),
      offY: Math.max(-1.5, Math.min(1.5, (cell.offY || 0) + dy)),
    });
  const setZoom = (v) => onChangeTransform({ zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, v)) });

  // for a TEXT cell, apply style change immediately
  const applyStyle = (patch) => {
    const next = { fontFamily: family, bold, color, ...patch };
    if (patch.fontFamily !== undefined) setFamily(patch.fontFamily);
    if (patch.bold !== undefined) setBold(patch.bold);
    if (patch.color !== undefined) setColor(patch.color);
    if (cell.type === "text") {
      onSetText({ text: textValue.trim() || cell.text, ...next });
    }
  };

  return (
    <div data-testid="editor-inspector-panel"
      className={`flex h-full flex-col overflow-y-auto rounded-2xl border p-5 shadow-[0_1px_2px_rgba(15,23,42,0.06)] ${isDark ? "border-[#29466f] bg-[#07132e] text-slate-100" : "border-[#c7dcf8] bg-[#f0f6ff] text-slate-800"}`}>
      <div className="flex items-center justify-between">
        <span data-testid="editor-selected-position-label" className="font-display font-semibold">{posLabel}</span>
        <span className={`rounded-full border px-2 py-0.5 text-xs ${isDark ? "border-[#29466f] bg-[#152541] text-slate-300" : "border-slate-200 bg-slate-100 text-slate-600"}`}>
          {cell.type === "image" ? (isEnglish ? "Image" : "Gambar") : cell.type === "text" ? (isEnglish ? "Text" : "Teks") : cell.type === "color" ? (isEnglish ? "Color" : "Warna") : (isEnglish ? "Empty" : "Kosong")}
        </span>
      </div>

      <Separator className="my-4 bg-blue-400/30" />

      <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="inspector-file-input"
        onChange={(e) => { if (e.target.files?.[0]) onUploadImage(e.target.files[0]); e.target.value = ""; }} />

      {cell.type === "empty" && (
        <Tabs defaultValue="image" className="w-full">
          <TabsList className={`grid w-full grid-cols-3 ${
            isDark
              ? "bg-[#152541] text-slate-300"
              : "bg-[#dbeafe] text-[#334155]"
          }`}>
            <TabsTrigger value="image" data-testid="inspector-tab-image" className="data-[state=active]:!bg-[#2563eb] data-[state=active]:!text-white data-[state=active]:!shadow-none">{isEnglish ? "Image" : "Gambar"}</TabsTrigger>
            <TabsTrigger value="text" data-testid="inspector-tab-text" className="data-[state=active]:!bg-[#2563eb] data-[state=active]:!text-white data-[state=active]:!shadow-none">{isEnglish ? "Text" : "Teks"}</TabsTrigger>
            <TabsTrigger value="color" data-testid="inspector-tab-color" className="data-[state=active]:!bg-[#2563eb] data-[state=active]:!text-white data-[state=active]:!shadow-none">{isEnglish ? "Color" : "Warna"}</TabsTrigger>
          </TabsList>
          <TabsContent value="image" className="mt-4">
            <Button className="w-full rounded-xl pms-inspector-upload-button" style={{ backgroundColor: "#2563eb", color: "#ffffff", border: "1px solid #3b82f6" }} data-testid="inspector-upload-button"
              onClick={() => fileRef.current?.click()}>
              <ImagePlus className="mr-2 h-4 w-4" /> {isEnglish ? "Upload image" : "Unggah gambar"}
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">{isEnglish ? "The image is cropped to fit inside the inner circle." : "Gambar dipangkas ke dalam lingkaran dalam."}</p>
          </TabsContent>
          <TabsContent value="text" className="mt-4 space-y-4">
            <Input placeholder="mis. ORD-1023" value={textValue}
              className={isDark
                ? "border-[#29466f] bg-[#0b1733] text-slate-100 placeholder:text-slate-500 focus-visible:ring-blue-500"
                : "border-[#bfdbfe] bg-white text-[#17345d] placeholder:text-[#94a3b8] focus-visible:ring-[#93c5fd]"}
              data-testid="editor-position-text-input"
              onChange={(e) => setTextValue(e.target.value)} />
            <TextStyleControls isDark={isDark} family={family} bold={bold} color={color}
              onChange={(patch) => {
                if (patch.fontFamily !== undefined) setFamily(patch.fontFamily);
                if (patch.bold !== undefined) setBold(patch.bold);
                if (patch.color !== undefined) setColor(patch.color);
              }} />
            <Button
              className="w-full rounded-xl pms-inspector-upload-button"
              style={{ backgroundColor: "#2563eb", color: "#ffffff", border: "1px solid #3b82f6" }}
              data-testid="inspector-set-text-button"
              disabled={!textValue.trim()}
              onClick={() => onSetText({ text: textValue.trim(), fontFamily: family, bold, color })}>
              <TypeIcon className="mr-2 h-4 w-4" /> {isEnglish ? "Add text" : "Tambahkan teks"}
            </Button>
          </TabsContent>
          <TabsContent value="color" className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <span className="text-xs font-semibold tracking-wide text-muted-foreground">{isEnglish ? "CIRCLE COLOR" : "WARNA LINGKARAN"}</span>
              <div className="flex items-center gap-2">
                <input type="color" value={pinColor}
                  data-testid="inspector-pin-color-input"
                  onChange={(e) => setPinColor(e.target.value)}
                  className={`h-9 w-10 cursor-pointer rounded-lg border bg-transparent p-1 ${
                  isDark ? "border-[#29466f]" : "border-[#bfdbfe]"
                }`} />
                <div className="flex flex-wrap gap-1.5">
                  {COLOR_SWATCHES.map((c) => (
                    <button key={c} type="button" data-testid={`inspector-pin-color-swatch-${c}`}
                      onClick={() => setPinColor(c)}
                      className={`h-6 w-6 rounded-full border ${
                        isDark ? "border-[#64748b]" : "border-[#94a3b8]"
                      } ${pinColor === c ? "ring-2 ring-primary ring-offset-1" : ""}`}
                      style={{ background: c }} />
                  ))}
                </div>
              </div>
            </div>
            <Button className="w-full rounded-xl" data-testid="inspector-set-color-button"
              onClick={() => onSetColor(pinColor)}>
              <PaintBucket className="mr-2 h-4 w-4" /> {isEnglish ? "Apply color" : "Beri warna"}
            </Button>
            <p className="text-xs text-muted-foreground">Lingkaran berwarna dihitung sudah terisi, jadi upload berikutnya lanjut ke nomor kosong.</p>
          </TabsContent>
        </Tabs>
      )}

      {cell.type === "color" && (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <span className="text-xs font-semibold tracking-wide text-muted-foreground">{isEnglish ? "CIRCLE COLOR" : "WARNA LINGKARAN"}</span>
            <div className="flex items-center gap-2">
              <input type="color" value={pinColor}
                data-testid="inspector-pin-color-input"
                onChange={(e) => { setPinColor(e.target.value); onSetColor(e.target.value); }}
                className={`h-9 w-10 cursor-pointer rounded-lg border bg-transparent p-1 ${
                  isDark ? "border-[#29466f]" : "border-[#bfdbfe]"
                }`} />
              <div className="flex flex-wrap gap-1.5">
                {COLOR_SWATCHES.map((c) => (
                  <button key={c} type="button" data-testid={`inspector-pin-color-swatch-${c}`}
                    onClick={() => { setPinColor(c); onSetColor(c); }}
                    className={`h-6 w-6 rounded-full border ${
                        isDark ? "border-[#64748b]" : "border-[#94a3b8]"
                      } ${pinColor === c ? "ring-2 ring-primary ring-offset-1" : ""}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
          </div>
          <Button variant="ghost" className="w-full rounded-xl text-destructive"
            data-testid="editor-delete-button" onClick={onDelete}>
            <Trash2 className="mr-2 h-4 w-4" /> {isEnglish ? "Remove color" : "Hapus warna"}
          </Button>
        </div>
      )}

      {cell.type === "text" && (
        <div className="space-y-4">
          <Input value={textValue} className={`border focus-visible:ring-blue-500 ${isDark ? "border-[#29466f] bg-[#0b1733] text-slate-100 placeholder:text-slate-400" : "border-slate-300 bg-white text-slate-800 placeholder:text-slate-400"}`} data-testid="editor-position-text-input"
            onChange={(e) => setTextValue(e.target.value)}
            onBlur={() => onSetText({ text: textValue.trim(), fontFamily: family, bold, color })} />
          <TextStyleControls isDark={isDark} family={family} bold={bold} color={color} onChange={applyStyle} />
          <Button variant="outline" className="w-full rounded-xl border border-blue-500 bg-[#2563eb] text-white hover:bg-[#1d4ed8] hover:text-white" data-testid="inspector-update-text-button"
            onClick={() => onSetText({ text: textValue.trim(), fontFamily: family, bold, color })}>
            {isEnglish ? "Update text" : "Perbarui teks"}
          </Button>
          <Button variant="outline" className={`w-full rounded-xl border border-red-300 ${isDark ? "bg-[#2a1020] text-red-400 hover:border-red-400 hover:bg-red-950/50 hover:text-red-300" : "bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700"}`}
            data-testid="editor-delete-button" onClick={onDelete}>
            <Trash2 className="mr-2 h-4 w-4" /> {isEnglish ? "Remove text" : "Hapus teks"}
          </Button>
        </div>
      )}

      {cell.type === "image" && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-2">
            <Button variant="secondary" className="rounded-xl pms-inspector-replace-button" style={{ backgroundColor: "#2563eb", color: "#ffffff", border: "1px solid #3b82f6" }} data-testid="editor-replace-image-button"
              onClick={() => fileRef.current?.click()}>
              <Replace className="mr-2 h-4 w-4" /> Ganti
            </Button>
            <Button variant="outline" className={`rounded-xl border border-red-300 ${isDark ? "bg-[#2a1020] text-red-400 hover:border-red-400 hover:bg-red-950/50 hover:text-red-300" : "bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700"}`} data-testid="editor-delete-image-button"
              onClick={onDelete}>
              <Trash2 className="mr-2 h-4 w-4" /> Hapus
            </Button>
          </div>
          <Button variant="outline" className={`w-full rounded-xl border ${isDark ? "border-[#29466f] bg-[#0b1733] text-slate-100 hover:bg-[#152541] hover:text-white" : "border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900"}`} data-testid="editor-crop-image-button"
            onClick={() => onCrop?.()}>
            <CropIcon className="mr-2 h-4 w-4" /> {isEnglish ? "Crop image" : "Crop gambar"}
          </Button>
          <Button variant="outline" className={`w-full rounded-xl border ${isDark ? "border-[#29466f] bg-[#0b1733] text-slate-100 hover:bg-[#152541] hover:text-white" : "border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900"}`} data-testid="editor-duplicate-image-button"
            onClick={() => onDuplicate?.()}>
            <CopyPlus className="mr-2 h-4 w-4" /> {isEnglish ? "Duplicate image" : "Duplikat gambar"}
          </Button>
          <div className="space-y-1.5">
            <span className="text-xs font-semibold tracking-wide text-muted-foreground">{isEnglish ? "BACKGROUND COLOR" : "WARNA LATAR"}</span>
            <div className="flex items-center gap-2">
              <input type="color" value={cell.bgColor || "#ffffff"}
                data-testid="inspector-bg-color-input"
                onChange={(e) => onChangeTransform({ bgColor: e.target.value })}
                className={`h-9 w-10 cursor-pointer rounded-lg border p-1 ${isDark ? "border-[#29466f] bg-[#0b1733]" : "border-slate-300 bg-white"}`} />
              <div className="flex flex-wrap gap-1.5">
                {COLOR_SWATCHES.map((c) => (
                  <button key={c} type="button" data-testid={`inspector-bg-color-swatch-${c}`}
                    onClick={() => onChangeTransform({ bgColor: c })}
                    className={`h-6 w-6 rounded-full border ${(cell.bgColor || "#ffffff") === c ? "ring-2 ring-primary ring-offset-1" : ""}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{isEnglish ? "Fills white areas when the image is zoomed out. Also appears in the PDF." : "Mengisi bagian putih saat gambar di-zoom out. Ikut tercetak di PDF."}</p>
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold tracking-wide text-muted-foreground">ZOOM</span>
              <span className="font-mono-num text-xs">{zoom.toFixed(2)}×</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" className={`h-9 w-9 rounded-xl border ${isDark ? "border-[#29466f] bg-[#0b1733] text-slate-100 hover:bg-[#152541] hover:text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900"}`}
                data-testid="editor-zoom-out-button" onClick={() => setZoom(zoom - 0.2)}>
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Slider data-testid="editor-zoom-slider" value={[zoom]} min={MIN_ZOOM} max={MAX_ZOOM} step={0.05}
                onValueChange={(v) => setZoom(v[0])} className="flex-1" />
              <Button variant="outline" size="icon" className={`h-9 w-9 rounded-xl border ${isDark ? "border-[#29466f] bg-[#0b1733] text-slate-100 hover:bg-[#152541] hover:text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900"}`}
                data-testid="editor-zoom-in-button" onClick={() => setZoom(zoom + 0.2)}>
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div>
            <span className="mb-2 block text-xs font-semibold tracking-wide text-muted-foreground">{isEnglish ? "POSITION" : "GESER POSISI"}</span>
            <div className="mx-auto grid w-32 grid-cols-3 gap-2">
              <div />
              <Button variant="outline" size="icon" className={`h-9 w-9 rounded-xl border ${isDark ? "border-[#29466f] bg-[#0b1733] text-slate-100 hover:bg-[#152541] hover:text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900"}`}
                data-testid="editor-nudge-up" onClick={() => nudge(0, -NUDGE)}><ArrowUp className="h-4 w-4" /></Button>
              <div />
              <Button variant="outline" size="icon" className={`h-9 w-9 rounded-xl border ${isDark ? "border-[#29466f] bg-[#0b1733] text-slate-100 hover:bg-[#152541] hover:text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900"}`}
                data-testid="editor-nudge-left" onClick={() => nudge(-NUDGE, 0)}><ArrowLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" className={`h-9 w-9 rounded-xl border ${isDark ? "border-[#29466f] bg-[#0b1733] text-slate-100 hover:bg-[#152541] hover:text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900"}`}
                data-testid="editor-reset-transform-button" onClick={onResetTransform}><RotateCcw className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" className={`h-9 w-9 rounded-xl border ${isDark ? "border-[#29466f] bg-[#0b1733] text-slate-100 hover:bg-[#152541] hover:text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900"}`}
                data-testid="editor-nudge-right" onClick={() => nudge(NUDGE, 0)}><ArrowRight className="h-4 w-4" /></Button>
              <div />
              <Button variant="outline" size="icon" className={`h-9 w-9 rounded-xl border ${isDark ? "border-[#29466f] bg-[#0b1733] text-slate-100 hover:bg-[#152541] hover:text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900"}`}
                data-testid="editor-nudge-down" onClick={() => nudge(0, NUDGE)}><ArrowDown className="h-4 w-4" /></Button>
              <div />
            </div>
            <p className="mt-3 text-center text-xs text-muted-foreground">{isEnglish ? "Tip: drag the image directly to reposition it." : "Tip: seret langsung gambar untuk menggeser."}</p>
          </div>
        </div>
      )}
    </div>
  );
}
